import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cssUrl } from '../utils/cssVersion';
import { useUser } from '../utils/UserContext';
import { hasSectionAccess, itemVisible, getRoleAccent } from '../data/map';
import { shelvesForUser, youtubeThumb, DEFAULT_THUMB } from '../data/videoLibrary';
import VideoPlayer from './VideoPlayer';

/*
 * VideoShelf — horizontal rows of video cards on ProfileHome (above the navigation).
 * One row per shelf the user may see (data/videoLibrary.js ↔ map.js section access).
 * Tap a card → the same modal player as the login screen. Swipe (touch) or wheel-scroll,
 * plus a ‹ dots › control row under every shelf on all devices (one dot per page of cards).
 */
const useStylesheet = (href) => {
  useEffect(() => {
    const key = href.split('?')[0];
    if (document.querySelector(`link[data-vs-css="${key}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = href; link.dataset.vsCss = key;
    document.head.appendChild(link);
  }, [href]);
};

const Card = ({ v, accent, onPlay }) => {
  const soon = !v.url;
  return (
    <button type="button" className={`vs-card ${soon ? 'vs-card-soon' : ''}`} onClick={() => onPlay(v)} aria-label={soon ? `${v.title} (coming soon)` : `Play ${v.title}`}>
      <div className="vs-thumb" style={{ backgroundImage: `url(${v.image || youtubeThumb(v.url) || DEFAULT_THUMB})` }}>
        {soon
          ? <span className="vs-soon">Coming soon</span>
          : <span className="vs-play" style={{ background: accent }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#0b1220"><path d="M8 5v14l11-7z" /></svg>
            </span>}
        {v.minutes ? <span className="vs-min">{v.minutes} min</span> : null}
      </div>
      <div className="vs-meta">
        <span className="vs-title">{v.title}</span>
        <span className="vs-summary">{v.summary}</span>
      </div>
    </button>
  );
};

const Row = ({ shelf, accent, onPlay }) => {
  const ref = useRef(null);
  const [canL, setCanL] = useState(false);
  const [canR, setCanR] = useState(false);
  const [pages, setPages] = useState(1);                   // how many viewport-widths the row spans
  const [cur, setCur] = useState(0);                       // the page currently in view
  /* Dots are PAGES, not cards. On a desktop pane ~3 cards fit, so 6 videos = 2 dots; on a
     phone one card fits, so it is one dot per video. The old one-dot-per-card version lit
     round(scrollLeft / cardWidth), which skipped dots when an arrow moved a viewport and
     could never reach the last ones — the track stops scrolling once the final card is in
     view, so there was nothing to scroll those dots to. */
  const geometry = () => {
    const el = ref.current; if (!el) return null;
    const first = el.firstElementChild;
    const gap = parseFloat(getComputedStyle(el).columnGap) || 14;
    const step = first ? first.offsetWidth + gap : 1;      // card width + gap
    const perView = Math.max(1, Math.floor((el.clientWidth + gap) / step));
    return { el, step, perView, pages: Math.max(1, Math.ceil(shelf.videos.length / perView)) };
  };
  const measure = () => {
    const g = geometry(); if (!g) return;
    const { el, step, perView } = g;
    const maxLeft = el.scrollWidth - el.clientWidth;
    setCanL(el.scrollLeft > 4);
    setCanR(el.scrollLeft < maxLeft - 4);
    setPages(g.pages);
    const page = el.scrollLeft >= maxLeft - 4 ? g.pages - 1 : Math.round(el.scrollLeft / (step * perView));
    setCur(Math.min(g.pages - 1, Math.max(0, page)));
  };
  const goTo = (page) => {
    const g = geometry(); if (!g) return;
    const i = Math.min(shelf.videos.length - 1, Math.max(0, page) * g.perView);
    const card = g.el.children[i]; if (card) g.el.scrollTo({ left: card.offsetLeft - g.el.offsetLeft, behavior: 'smooth' });
  };
  const nudge = (dir) => goTo(cur + dir);                  // one page per arrow press
  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    // the stylesheet is appended at runtime, so the cards get their width after mount —
    // re-measure when the track or its first card changes size
    let ro;
    if (typeof ResizeObserver !== 'undefined' && ref.current) {
      ro = new ResizeObserver(measure);
      ro.observe(ref.current);
      if (ref.current.firstElementChild) ro.observe(ref.current.firstElementChild);
    }
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, [shelf.videos.length]);                               // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="vs-shelf" style={{ '--vs-accent': accent }}>
      <div className="vs-head">
        <h3 className="vs-heading">{shelf.heading}</h3>
        <span className="vs-count">{shelf.videos.length} {shelf.videos.length === 1 ? 'video' : 'videos'}</span>
      </div>
      <div className="vs-track-wrap">
        <div className="vs-track" ref={ref} onScroll={measure}>
          {shelf.videos.map((v) => <Card key={v.id} v={v} accent={accent} onPlay={onPlay} />)}
        </div>
      </div>
      {pages > 1 && (
        <div className="vs-nav">
          {/* one control row under the cards: ‹ dots › — same on desktop and touch, never covers a thumbnail */}
          <button type="button" className="vs-arrow" onClick={() => nudge(-1)} disabled={!canL} aria-label="Previous videos">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <div className="vs-dots" role="tablist" aria-label="Pages of videos">
            {Array.from({ length: pages }, (_, i) => (
              <button key={i} type="button" role="tab" aria-selected={i === cur} aria-label={`Page ${i + 1} of ${pages}`}
                className={`vs-dot ${i === cur ? 'on' : ''}`} onClick={() => goTo(i)} />
            ))}
          </div>
          <button type="button" className="vs-arrow" onClick={() => nudge(1)} disabled={!canR} aria-label="Next videos">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      )}
    </section>
  );
};

/* Props are optional. With none, this is the ProfileHome shelf (the user's tier row from
   data/videoLibrary.js, role accent). A page can hand in its own `shelves` (+ `accent`) instead —
   VideoAvailableLibrary does, for the availability tabs — and gets the same cards and player. */
const VideoShelf = ({ shelves: given = null, accent: givenAccent = null, className = '' }) => {
  useStylesheet(cssUrl('videoShelf.css'));
  const { user } = useUser();
  const accent = givenAccent || getRoleAccent(user);
  const own = useMemo(() => shelvesForUser(user, { hasSectionAccess, itemVisible }), [user]);
  const shelves = given || own;
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    if (!playing) return undefined;
    const onKey = (e) => e.key === 'Escape' && setPlaying(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playing]);

  if (!shelves.length) return null;

  return (
    <div className={`vs-root ${className}`.trim()}>
      {shelves.map((s) => <Row key={s.id} shelf={s} accent={accent} onPlay={setPlaying} />)}
      {playing && createPortal(
        <div className="vs-overlay" onClick={() => setPlaying(null)}>
          <div className="vs-modal" style={{ borderColor: accent }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={playing.title}>
            <div className="vs-modal-head">
              <div>
                <h3 className="vs-modal-title" style={{ color: accent }}>{playing.title}</h3>
                {playing.summary && <p className="vs-modal-sub">{playing.summary}</p>}
              </div>
              <button type="button" className="vs-close" onClick={() => setPlaying(null)} aria-label="Close video"><X className="w-5 h-5" /></button>
            </div>
            <div className="vs-frame">
              <VideoPlayer url={playing.url} title={playing.title} autoPlay muted={false} />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default VideoShelf;